import React from "react"
import { ChevronUpIcon, ChevronDownIcon, FunnelIcon, ArrowUpIcon, ArrowDownIcon } from "@heroicons/react/24/outline"
import CardFrame from "./CardFrame"
import { formatValue, formatChange } from "../format"
import useDashboardTexts from "../useDashboardTexts"

// a date as a table cell fits it: 28.09.26 20:19
const pad = (n) => String(n).padStart(2, "0")
function compactDate(value) {
  const d = new Date(value)
  if (isNaN(d)) return ""
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${String(d.getFullYear()).slice(2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const cell = (value, column, change = null) => {
  if (value === null || value === undefined || value === "") return "—"
  if (column.type === "date") return compactDate(value)
  if (column.type === "number" && column.bar) return <Bar value={value} column={column} change={change} />
  if (column.type === "number" && change) return <>{formatValue(value, column.unit || "number")}{change}</>
  if (column.type === "number") return formatValue(value, column.unit || "number")
  return String(value)
}

// a number's change from the compared period (a table with def.compare): small, beside the figure,
// green with an arrow up, red with an arrow down; hover for the earlier figure
function Change({ value, previous, column }) {
  const change = formatChange(value, previous)
  if (!change) return null
  const now = Number(value) || 0
  const before = Number(previous) || 0
  const Arrow = now > before ? ArrowUpIcon : now < before ? ArrowDownIcon : null
  return (
    // a slot of one width, the change at its start: the figures before it stay lined up down the column
    <span className={`inline-flex items-center gap-0.5 w-[64px] pl-2 shrink-0 text-left text-[11px] font-semibold ${change.className}`} title={formatValue(before, column.unit || "number")} data-testid="table-change">
      {Arrow && <Arrow className="w-3 h-3" strokeWidth={2.5} />}
      {change.text.replace(/^[+−]/, "")}
    </span>
  )
}

// a percent column the table draws as a bar (def column `bar: true`): a funnel read at a glance
function Bar({ value, column, change }) {
  const width = Math.max(0, Math.min(100, Number(value) || 0))
  return (
    <span className="inline-flex items-center justify-end gap-2 w-full">
      <span className="relative h-2 w-[120px] rounded-full bg-admin_neutral_1 overflow-hidden" aria-hidden="true">
        <span className="absolute inset-y-0 left-0 rounded-full bg-admin_primary" style={{ width: `${width}%` }} />
      </span>
      <span className="w-[56px] shrink-0 text-right">{formatValue(value, column.unit || "percent")}</span>
      {change}
    </span>
  )
}

/**
 * Rows of a table source: the columns the card chose, sorted by a click on a
 * header (kept with the card), filtered and cut as the card says. A table's
 * own settings (def.params, set in the card editor) are named under its title.
 */
export default function TableCard({ card, def, data, loading, comparing, onEdit, onSort }) {
  const t = useDashboardTexts()
  const columns = data?.columns || []
  const sort = card.sort?.column ? card.sort : def?.defaultSort
  const clickSort = (id) => onSort?.({ column: id, direction: sort?.column === id && sort.direction === "desc" ? "asc" : "desc" })
  const filters = (card.filters || []).filter((f) => f.column && f.value !== "" && f.value !== undefined)
  // the settings the rows were read with: "Stock at or below 20"
  const settings = (def?.params || []).filter((p) => data?.params?.[p.id] !== undefined).map((p) => `${t(p.label)} ${data.params[p.id]}`)

  return (
    <CardFrame
      title={(card.title && t(card.title)) || t(def?.label) || card.table}
      subtitle={data ? `${t("%s of %s", Math.min(data.total, card.limit || 10), data.total)}${settings.map((text) => ` · ${text}`).join("")}${filters.length ? ` · ${filters.length === 1 ? t("%s filter", filters.length) : t("%s filters", filters.length)}` : ""}` : ""}
      onEdit={onEdit}
      testId="dashboard-table"
    >
      {data?.error ? (
        <span className="text-sm text-admin_error">{t`Could not load`}</span>
      ) : (
        <div className={`overflow-x-auto -mx-4 ${loading ? "opacity-40" : ""}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-admin_bordered">
                {columns.map((column) => {
                  const on = sort?.column === column.id
                  const Arrow = on && sort.direction === "asc" ? ChevronUpIcon : ChevronDownIcon
                  return (
                    <th key={column.id} className={`px-4 py-2 font-semibold text-[11px] uppercase tracking-wide text-admin_text_secondary whitespace-nowrap ${column.type === "number" ? "text-right" : "text-left"}`}>
                      <button type="button" onClick={() => clickSort(column.id)} className={`inline-flex items-center gap-1 hover:text-admin_text ${on ? "text-admin_text" : ""}`}>
                        {t(column.label)}
                        <Arrow className={`w-3 h-3 ${on ? "" : "opacity-0"}`} />
                      </button>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((row, i) => (
                <tr key={row._key || i} className="border-b border-admin_bordered last:border-b-0 hover:bg-admin_neutral_1/40">
                  {columns.map((column) => (
                    <td key={column.id} className={`px-4 py-2 text-admin_text ${column.type === "number" ? "text-right tabular-nums whitespace-nowrap" : "max-w-[280px] truncate"}`}>
                      {cell(
                        row[column.id],
                        column,
                        comparing && column.type === "number" && row._previous && column.id in row._previous ? (
                          <Change value={row[column.id]} previous={row._previous[column.id]} column={column} />
                        ) : null
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              {data && !data.rows?.length && (
                <tr>
                  <td colSpan={columns.length || 1} className="px-4 py-6 text-center text-admin_text_secondary">
                    {filters.length ? (
                      <span className="inline-flex items-center gap-1"><FunnelIcon className="w-4 h-4" />{t`Nothing matches the filters`}</span>
                    ) : (
                      t(data.empty) || t`Nothing in this period`
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </CardFrame>
  )
}
